import './Login.scss';

import { useEffect, useRef, useState } from 'react';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import GoogleG from '@/assets/storage/GoogleG.svg';
import LogoBlack from '@/assets/storage/LogoBlack.svg';
import LogoWhite from '@/assets/storage/LogoWhite.svg';
import { CButton } from '@/components/CButton';
import { LanguageSwitcher } from '@/components/general/LanguageSwitcher';
import { HeaderLogo } from '@/components/HeaderLogo';
import { Icon } from '@/components/Icon';
import { ThemeToggle } from '@/components/ThemeToggle';
import { toastError } from '@/components/Toast';
import { googleClientId } from '@/core/infrastructure/authConfig';
import { FooterLinks } from '@/modules/auth/components/FooterLinks';
import { type AppDispatch, type RootState } from '@/store/store';
import {
  loadGoogleIdentityServices,
  type GoogleCredentialResponse,
  type GoogleIdentityServices,
} from '../application/googleIdentity';
import { googleLoginUser, type GoogleLoginError } from '../application/loginUser';

type LoginErrorCode = GoogleLoginError['code'] | 'GoogleClientIdMissing' | 'GoogleScriptFailed';

const errorTranslationKeys: Record<LoginErrorCode, string> = {
  GoogleCredentialMissing: 'google_credential_missing',
  InvalidToken: 'invalid_token',
  TokenMissing: 'token_missing',
  ServiceUnavailable: 'service_unavailable_message_google',
  GoogleLoginFailed: 'google_login_failed',
  GoogleClientIdMissing: 'google_client_id_missing',
  GoogleScriptFailed: 'google_script_failed',
};

const getErrorTranslationKey = (code: LoginErrorCode) =>
  `pages/login:pages.login.errors.${errorTranslationKeys[code]}`;

type GoogleStatus = 'loading' | 'ready' | 'error';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getLoginErrorCode = (error: unknown): LoginErrorCode => {
  const code = isRecord(error) && typeof error.code === 'string' ? error.code : null;

  if (code && Object.hasOwn(errorTranslationKeys, code)) {
    return code as LoginErrorCode;
  }

  return 'GoogleLoginFailed';
};

export default function Login() {
  const { t, i18n } = useTranslation(['pages/login', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const themeMode = useSelector((state: RootState) => state.theme.themeMode);
  const isLoggingIn = useSelector((state: RootState) => state.auth.status === 'loading');
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus>('loading');
  const [retryToken, setRetryToken] = useState(0);
  const [errorCode, setErrorCode] = useState<LoginErrorCode | null>(null);
  const googleServicesRef = useRef<GoogleIdentityServices | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const hasLoginStartedRef = useRef(false);
  const isMountedRef = useRef(true);
  const activeLanguage = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'tr';
  const currentDateParts = new Intl.DateTimeFormat(activeLanguage === 'en' ? 'en-US' : 'tr-TR', {
    day: 'numeric',
    month: 'long',
  }).formatToParts(new Date());
  const currentDay = currentDateParts.find(({ type }) => type === 'day')?.value ?? '';
  const currentMonth = currentDateParts.find(({ type }) => type === 'month')?.value ?? '';
  const latestRef = useRef({ dispatch, navigate, t });

  useEffect(() => {
    latestRef.current = { dispatch, navigate, t };
  });

  useEffect(() => {
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let initializedGoogle: GoogleIdentityServices | null = null;

    setGoogleStatus('loading');
    setErrorCode(null);
    hasLoginStartedRef.current = false;

    const initializeGoogleAuth = async () => {
      if (!googleClientId) {
        if (isMounted) {
          setGoogleStatus('error');
          setErrorCode('GoogleClientIdMissing');
        }
        return;
      }

      try {
        const google = await loadGoogleIdentityServices(activeLanguage);

        if (!isMounted) {
          return;
        }

        google.accounts.id.initialize({
          client_id: googleClientId,
          auto_select: false,
          context: 'signin',
          itp_support: true,
          use_fedcm_for_button: true,
          callback: (response: GoogleCredentialResponse) => {
            if (!isMounted || !isMountedRef.current) {
              return;
            }

            const { dispatch: dispatchLogin, navigate: goTo, t: translate } = latestRef.current;
            const credential = response.credential?.trim();

            if (!credential) {
              toastError(translate(getErrorTranslationKey('GoogleCredentialMissing')));
              return;
            }

            if (hasLoginStartedRef.current) {
              return;
            }

            hasLoginStartedRef.current = true;

            void dispatchLogin(googleLoginUser({ credential }))
              .unwrap()
              .then(() => {
                if (!isMountedRef.current) {
                  return;
                }

                goTo('/dashboard-panel', {
                  replace: true,
                  state: { fromLogin: true },
                });
              })
              .catch((error: unknown) => {
                if (isMountedRef.current) {
                  const description =
                    isRecord(error) && typeof error.description === 'string'
                      ? error.description.trim()
                      : '';
                  toastError(
                    description || translate(getErrorTranslationKey(getLoginErrorCode(error)))
                  );
                }
              })
              .finally(() => {
                hasLoginStartedRef.current = false;
              });
          },
        });
        initializedGoogle = google;
        googleServicesRef.current = google;
        setGoogleStatus('ready');
      } catch {
        if (isMounted) {
          setGoogleStatus('error');
          setErrorCode('GoogleScriptFailed');
        }
      }
    };

    void initializeGoogleAuth();

    return () => {
      isMounted = false;

      if (googleServicesRef.current === initializedGoogle) {
        googleServicesRef.current = null;
      }
    };
  }, [activeLanguage, retryToken]);

  useEffect(() => {
    if (googleStatus !== 'ready') {
      return;
    }

    const google = googleServicesRef.current;
    const container = googleButtonRef.current;

    if (!google || !container) {
      return;
    }

    container.replaceChildren();

    try {
      google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: themeMode === 'light' ? 'outline' : 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: '320px',
        locale: activeLanguage,
      });
    } catch {
      setGoogleStatus('error');
      setErrorCode('GoogleScriptFailed');
    }
  }, [activeLanguage, googleStatus, themeMode]);

  const handleGoogleRetry = () => {
    setErrorCode(null);
    setGoogleStatus('loading');
    setRetryToken((token) => token + 1);
  };

  const errorMessage = errorCode ? t(getErrorTranslationKey(errorCode)) : null;

  return (
    <div className="c-login__shell">
      <div className="c-login__stage">
        <div className="c-login__frame">
          <header className="c-login__header">
            <div className="c-login__brand">
              <HeaderLogo />
            </div>
            <div className="c-login__toolbar">
              <LanguageSwitcher />
              <ThemeToggle className="c-login__theme-toggle" />
            </div>
          </header>
          <main className="c-login__container">
            <section className="c-login__section-form">
              <div className="c-login__panel">
                <p className="c-login__eyebrow">{t('pages/login:pages.login.eyebrow')}</p>
                <h1 className="c-login__title">
                  <span className="c-login__title-accent">
                    {t('pages/login:pages.login.brand_title')}
                  </span>
                </h1>
                <p className="c-login__description">{t('pages/login:pages.login.description')}</p>
                <div className="c-login__login-options">
                  <div
                    className="c-login__google-button-wrapper"
                    role="group"
                    aria-label={t('pages/login:pages.login.google_sign_in')}
                  >
                    {googleStatus === 'error' ? (
                      <Alert
                        className="c-login__error"
                        message={errorMessage}
                        type="error"
                        showIcon
                        action={
                          <CButton variant="primary" size="sm" onClick={handleGoogleRetry}>
                            {t('common:common.try_again')}
                          </CButton>
                        }
                      />
                    ) : googleStatus === 'loading' ? (
                      <div
                        className="c-login__google-button-status"
                        role="status"
                        aria-label={t('pages/login:pages.login.google_sign_in_loading')}
                      >
                        <Spin size="small" />
                      </div>
                    ) : (
                      <div className="c-login__google-button">
                        <div ref={googleButtonRef} className="c-login__google-button-real" />
                        <div
                          aria-hidden="true"
                          className={`c-login__google-button-overlay${isLoggingIn ? ' c-login__google-button-overlay--loading' : ''}`}
                        >
                          <CButton
                            loading={isLoggingIn}
                            startIcon={<img src={GoogleG} alt="" />}
                            block
                            className="c-login__google-button-overlay-button"
                            size="md"
                            tabIndex={-1}
                            variant="primary"
                          >
                            {t('pages/login:pages.login.google_sign_in')}
                          </CButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </main>
          <footer className="c-login__footer">
            {activeLanguage === 'en' && (
              <span className="c-login__footer-copy">
                {t('pages/login:pages.login.footer.copy')}
              </span>
            )}
            <img
              alt={t('pages/login:pages.login.footer.logo_alt')}
              className="c-login__footer-logo"
              src={themeMode === 'dark' ? LogoWhite : LogoBlack}
            />
            {activeLanguage !== 'en' && (
              <span className="c-login__footer-copy">
                {t('pages/login:pages.login.footer.copy')}
              </span>
            )}
            <span className="c-login__footer-access">
              <span className="c-login__access-badge">
                <span aria-hidden="true" className="c-login__access-icon">
                  <SafetyCertificateOutlined />
                </span>
                <span className="c-login__access-label">
                  {t('pages/login:pages.login.secure_access')}
                </span>
              </span>
            </span>
          </footer>
        </div>
        <aside aria-labelledby="login-showcase-title" className="c-login__showcase">
          <div className="c-login__showcase-content">
            <p className="c-login__showcase-eyebrow">
              {t('pages/login:pages.login.showcase.eyebrow')}
            </p>
            <h2 id="login-showcase-title" className="c-login__showcase-title">
              {t('pages/login:pages.login.showcase.title')}
            </h2>
            <div className="c-login__showcase-card">
              <div className="c-login__showcase-panel">
                <div className="c-login__showcase-card-header">
                  <div>
                    <span className="c-login__showcase-card-label">
                      {t('pages/login:pages.login.showcase.card_label')}
                    </span>
                    <h3>{t('pages/login:pages.login.showcase.card_title')}</h3>
                  </div>
                  <span className="c-login__showcase-metric">
                    <strong>{currentDay}</strong>
                    <small>{currentMonth}</small>
                  </span>
                </div>
                <ul className="c-login__showcase-list">
                  <li className="c-login__showcase-item">
                    <span aria-hidden="true" className="c-login__showcase-item-icon">
                      <Icon name="upload" size={18} strokeWidth={1.8} />
                    </span>
                    <span className="c-login__showcase-item-copy">
                      <strong>{t('pages/login:pages.login.showcase.upload')}</strong>
                      <span>{t('pages/login:pages.login.showcase.upload_meta')}</span>
                    </span>
                  </li>
                  <li className="c-login__showcase-item">
                    <span aria-hidden="true" className="c-login__showcase-item-icon">
                      <Icon name="send" size={18} strokeWidth={1.8} />
                    </span>
                    <span className="c-login__showcase-item-copy">
                      <strong>{t('pages/login:pages.login.showcase.publish')}</strong>
                      <span>{t('pages/login:pages.login.showcase.publish_meta')}</span>
                    </span>
                  </li>
                  <li className="c-login__showcase-item">
                    <span aria-hidden="true" className="c-login__showcase-item-icon">
                      <Icon name="chartPieSlice" size={18} strokeWidth={1.8} />
                    </span>
                    <span className="c-login__showcase-item-copy">
                      <strong>{t('pages/login:pages.login.showcase.reporting')}</strong>
                      <span>{t('pages/login:pages.login.showcase.reporting_meta')}</span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>
      <FooterLinks />
    </div>
  );
}
